// Svelte 5 no longer needs svelte/store imports for basic reactivity
import online from "../configs/status.js";
import { v4 as uuidv4 } from 'uuid';
import { authState } from "./authStore.svelte.js";
import { environment } from "./envStore.svelte.js";

/* Firebase */
import { auth } from '@app/configs/firebase.js';
import db from '@app/configs/firestore.js';
import { collection, doc, getDocs, setDoc, deleteDoc } from 'firebase/firestore';

// Preset types
export const PRESET_TYPES = {
    SUBTITLE: 'subtitle',
    TRANSCRIPT: 'transcript',
    AUDIO_DESCRIPTION: 'audio-description',
    AUDIO_DUBBING: 'audio-dubbing',
    CCSL: 'ccsl',
    DIALOGUE_LIST: 'dialogue-list',
    VIDEO: 'video',
    EVENT_GROUP_STANDARDS: 'event-group-standards'
};

const LOCAL_STORAGE_KEY = "cc-export-presets";
const DEBUG_LOG_KEY = "cc-export-presets-debug";
const DEBUG_LOG_MAX_ENTRIES = 40;

/*
 * Production diagnostics for export presets.
 * Logs to the console with a stable "[export-presets]" prefix and keeps a
 * ring buffer of recent events in localStorage so support can ask users to
 * paste it (Help > copy localStorage["cc-export-presets-debug"]).
 * Never log preset config here - it may contain local file paths.
 */
function presetLog(level, event, details = {}) {
    const logFn = console[level] || console.info;
    logFn(`[export-presets] ${event}`, details);

    try {
        const entry = {
            t: new Date().toISOString(),
            event,
            online,
            electron: environment.electron,
            ...details
        };
        const buffer = JSON.parse(localStorage.getItem(DEBUG_LOG_KEY)) || [];
        buffer.push(entry);
        while (buffer.length > DEBUG_LOG_MAX_ENTRIES) {
            buffer.shift();
        }
        localStorage.setItem(DEBUG_LOG_KEY, JSON.stringify(buffer));
    } catch (error) {
        // Diagnostics must never break the app
    }
}

// Read presets from localStorage; corrupt JSON must not crash the module.
function readLocalPresets() {
    try {
        const presetData = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || [];
        return Array.isArray(presetData) ? presetData : [];
    } catch (error) {
        presetLog("error", "local-storage-parse-failed", { error: error.message });
        return [];
    }
}

function writeLocalPresets(presets) {
    try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(presets));
    } catch (error) {
        presetLog("error", "local-storage-write-failed", { error: error.message });
    }
}

// Firestore rejects nested `undefined` values (e.g. subtitle encodeOptions);
// a JSON round-trip strips them.
function stripUndefined(presetInfo) {
    return JSON.parse(JSON.stringify(presetInfo));
}

/*
 * Resolve the user id used for Firestore preset paths.
 * The load path has always used auth.currentUser.uid, so writes
 * must use the same id. Callers used to pass authState.user.id which, for
 * team members, is a team-user UUID - presets saved under it were never
 * found again on the next launch.
 */
function resolvePresetUserId(passedUserId) {
    if (!online) {
        return null;
    }

    const authUid = auth?.currentUser?.uid || null;

    if (passedUserId && authUid && passedUserId !== authUid) {
        presetLog("warn", "user-id-mismatch", {
            passedUserId,
            authUid
        });
    }

    return authUid;
}

function sortPresets(presets) {
    presets.sort((a, b) => {
        return (a.name || '').localeCompare(b.name || '');
    });
    return presets;
}

// Function to initialize preset state from localStorage
function initializePresetState() {
    return sortPresets(readLocalPresets());
}

// Create reactive state for export presets, initialized from localStorage
export const presetState = $state(initializePresetState());

function presetsCollection(userId) {
    return collection(db, "users", userId, "presets");
}

/*
 * Recovery for presets saved under the wrong id before the user-id fix:
 * 1. If the team-user record id differs from the auth uid, copy any presets
 *    found under users/{teamUserId}/presets to users/{authUid}/presets.
 * 2. Otherwise, if localStorage has presets (e.g. from a failed Firestore
 *    write that fell back to localStorage), upload those.
 * Returns { presets, source } or null when there is nothing to recover.
 */
async function recoverOrphanedPresets(authUid) {
    const teamUserId = authState.user?.id;

    if (teamUserId && teamUserId !== authUid) {
        try {
            const orphanSnapshot = await getDocs(presetsCollection(teamUserId));

            if (!orphanSnapshot.empty) {
                const orphanPresets = orphanSnapshot.docs.map(presetDoc => presetDoc.data());

                await Promise.all(orphanPresets.map(preset => {
                    if (!preset.id) {
                        preset.id = uuidv4();
                    }
                    return setDoc(doc(presetsCollection(authUid), preset.id), stripUndefined(preset));
                }));

                presetLog("info", "recovered-orphaned-presets", {
                    source: "firebase-orphan",
                    teamUserId,
                    authUid,
                    count: orphanPresets.length
                });

                return { presets: orphanPresets, source: "firebase-orphan" };
            }
        } catch (error) {
            presetLog("error", "orphan-recovery-failed", {
                teamUserId,
                authUid,
                error: error.code || error.message
            });
        }
    }

    const localPresets = readLocalPresets();

    if (localPresets.length > 0) {
        try {
            await Promise.all(localPresets.map(preset => {
                if (!preset.id) {
                    preset.id = uuidv4();
                }
                return setDoc(doc(presetsCollection(authUid), preset.id), stripUndefined(preset));
            }));

            presetLog("info", "recovered-orphaned-presets", {
                source: "localStorage",
                authUid,
                count: localPresets.length
            });

            return { presets: localPresets, source: "localStorage-migrated" };
        } catch (error) {
            presetLog("error", "local-migration-failed", {
                authUid,
                error: error.code || error.message
            });
            // Still show the local presets rather than an empty list
            return { presets: localPresets, source: "localStorage" };
        }
    }

    return null;
}

// Function to load presets from database or localStorage
export async function loadPresets(userId) {
    let presetData = [];
    let source = "localStorage";
    const authUid = resolvePresetUserId(userId);

    // Use local storage instead of firebase when online is set to false
    if (online && authUid) {
        try {
            let snapshot = await getDocs(presetsCollection(authUid));

            if (!snapshot.empty) {
                presetData = snapshot.docs.map(presetDoc => presetDoc.data());
                source = "firebase";
            } else {
                const recovered = await recoverOrphanedPresets(authUid);

                if (recovered) {
                    presetData = recovered.presets;
                    source = recovered.source;
                } else {
                    source = "empty";
                }
            }

            // Keep the local cache in sync with what we loaded
            writeLocalPresets(presetData);
        } catch (error) {
            presetLog("error", "load-failed", {
                authUid,
                error: error.code || error.message
            });
            // Fallback to localStorage on error
            presetData = readLocalPresets();
            source = "localStorage-fallback";
        }
    } else {
        presetData = readLocalPresets();
    }

    sortPresets(presetData);

    // Update the state by emptying the array and adding new items
    presetState.length = 0;
    presetData.forEach(item => presetState.push(item));

    presetLog("info", "load", {
        authUid,
        passedUserId: userId || null,
        source,
        count: presetData.length
    });
}

// Function to get presets by type
export function getPresetsByType(type) {
    return presetState.filter(preset => preset.type === type);
}

// Function to create a new preset
export async function createPreset(presetInfo, userId) {
    // Ensure the preset has an ID
    if (!presetInfo.id) {
        presetInfo.id = uuidv4();
    }
    
    // Add timestamp
    presetInfo.createdAt = new Date().toISOString();
    presetInfo.updatedAt = new Date().toISOString();

    const authUid = resolvePresetUserId(userId);
    let storage = "localStorage";

    // Use local storage instead of firebase when online is set to false
    if (online && authUid) {
        try {
            await setDoc(doc(presetsCollection(authUid), presetInfo.id), stripUndefined(presetInfo));
            storage = "firebase";
        } catch (error) {
            presetLog("error", "create-failed", {
                authUid,
                presetId: presetInfo.id,
                type: presetInfo.type,
                error: error.code || error.message
            });
            storage = "localStorage-fallback";
        }
    }

    // Add the new item to the array without reassigning
    presetState.push(presetInfo);

    // Always keep the localStorage cache in sync
    writeLocalPresets(presetState);

    presetLog("info", "create", {
        authUid,
        presetId: presetInfo.id,
        type: presetInfo.type,
        storage,
        count: presetState.length
    });
    
    return presetInfo;
}

// Function to remove a preset
export async function removePreset(presetId, userId) {
    const authUid = resolvePresetUserId(userId);
    let storage = "localStorage";

    // Use local storage instead of firebase when online is set to false
    if (online && authUid) {
        try {
            await deleteDoc(doc(presetsCollection(authUid), presetId));
            storage = "firebase";
        } catch (error) {
            presetLog("error", "remove-failed", {
                authUid,
                presetId,
                error: error.code || error.message
            });
            storage = "localStorage-fallback";
        }
    }
    
    // Filter in place without reassigning the state
    const itemsToKeep = presetState.filter(preset => preset.id !== presetId);
    presetState.length = 0;
    itemsToKeep.forEach(item => presetState.push(item));

    // Always update localStorage to keep it in sync
    writeLocalPresets(presetState);

    presetLog("info", "remove", {
        authUid,
        presetId,
        storage,
        count: presetState.length
    });
}

// Function to update an existing preset
export async function updatePreset(presetInfo, userId) {
    // Update timestamp
    presetInfo.updatedAt = new Date().toISOString();

    const authUid = resolvePresetUserId(userId);
    let storage = "localStorage";

    // Use local storage instead of firebase when online is set to false
    if (online && authUid) {
        try {
            await setDoc(doc(presetsCollection(authUid), presetInfo.id), stripUndefined(presetInfo));
            storage = "firebase";
        } catch (error) {
            presetLog("error", "update-failed", {
                authUid,
                presetId: presetInfo.id,
                type: presetInfo.type,
                error: error.code || error.message
            });
            storage = "localStorage-fallback";
        }
    }
    
    let presetIndex = presetState.findIndex(preset => preset.id === presetInfo.id);

    if (presetIndex !== -1) {
        // Update in place without reassigning the entire array
        presetState[presetIndex] = presetInfo;
    } else {
        // If not found, add it
        presetState.push(presetInfo);
    }
    
    // Always update localStorage to keep it in sync
    writeLocalPresets(presetState);

    presetLog("info", "update", {
        authUid,
        presetId: presetInfo.id,
        type: presetInfo.type,
        storage,
        count: presetState.length
    });
}

// Function to export presets as JSON
export function exportPresetsAsJson(presetIds = null) {
    let presetsToExport;
    
    if (presetIds && Array.isArray(presetIds)) {
        presetsToExport = presetState.filter(preset => presetIds.includes(preset.id));
    } else {
        presetsToExport = [...presetState];
    }
    
    return JSON.stringify(presetsToExport, null, 2);
}

// Function to import presets from JSON
export async function importPresetsFromJson(jsonString, userId) {
    try {
        const importedPresets = JSON.parse(jsonString);
        
        if (!Array.isArray(importedPresets)) {
            throw new Error("Invalid preset format: expected an array");
        }
        
        const results = {
            success: [],
            failed: [],
            duplicates: []
        };
        
        for (const preset of importedPresets) {
            // Validate preset structure
            if (!preset.type || !preset.config) {
                results.failed.push({
                    preset,
                    reason: "Missing required fields (type or config)"
                });
                continue;
            }
            
            // Check for duplicate names
            const existingPreset = presetState.find(p => 
                p.name === preset.name && p.type === preset.type
            );
            
            if (existingPreset) {
                results.duplicates.push(preset);
                // Generate a new name for the duplicate
                preset.name = `${preset.name} (imported)`;
            }
            
            // Create new ID for imported preset
            const newPreset = {
                ...preset,
                id: uuidv4(),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };
            delete newPreset.builtin;
            
            try {
                await createPreset(newPreset, userId);
                results.success.push(newPreset);
            } catch (error) {
                results.failed.push({
                    preset,
                    reason: error.message
                });
            }
        }

        presetLog("info", "import", {
            success: results.success.length,
            duplicates: results.duplicates.length,
            failed: results.failed.length
        });
        
        return results;
    } catch (error) {
        presetLog("error", "import-failed", { error: error.message });
        throw new Error(`Failed to import presets: ${error.message}`);
    }
}

// Function to get a preset by ID
export function getPresetById(presetId) {
    return presetState.find(preset => preset.id === presetId);
}
